import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BtnBase } from 'Components';
import {
    CERTIFICATE_CATEGORIES,
    CERTIFICATE_FILE_ACCEPT,
    LIST_CREATE_EMPTY,
    LIST_CREATE_EXCEL,
    LIST_CREATE_MANUAL,
    NO_CERTIFICATE_CATEGORY,
} from 'Constants/CertificateNames';
import {
    createCertificateNameList,
    previewCertificateNameFile,
    updateCertificateNameList,
} from 'Services/CertificateNamesSvc';
import ImportSummary from './ImportSummary';
import { apiErrorMessage, fieldErrors } from './CertificateHelpers';

const EMPTY_FORM = {
    name: '',
    description: '',
    isActive: true,
    names: '',
    category: NO_CERTIFICATE_CATEGORY,
    file: null,
};

/**
 * Create a certificate name list, or edit one's name and description.
 *
 * `mode` picks how a new list starts:
 *   empty  - a name and nothing else; fill it later.
 *   manual - names typed in, one per line.
 *   excel  - a spreadsheet, checked on the server (a dry run) as soon as it is
 *            chosen, so the operator sees what will go in before creating.
 * With a `record`, the dialog edits that list instead.
 *
 * After a create that brought names with it, the dialog stays open on the
 * server's summary of what was imported; Close then hands control back.
 */
const CertificateListFormModal = ({ open, mode, record, onClose, onSaved }) => {
    const { t } = useTranslation();
    const isEdit = Boolean(record);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        setForm(isEdit ? { ...EMPTY_FORM, name: record.name, description: record.description ?? '' } : EMPTY_FORM);
        setErrors({});
        setPreview(null);
        setResult(null);
    }, [open, isEdit, record]);

    const set = (key) => (event) => {
        const value = event?.target ? event.target.value : event;
        setForm((previous) => ({ ...previous, [key]: value }));
        setErrors((previous) => ({ ...previous, [key]: undefined }));
    };

    const handleFile = async (event) => {
        const file = event.target.files?.[0] ?? null;
        // Clearing the input lets the same file be chosen again after a fix.
        event.target.value = '';

        setForm((previous) => ({ ...previous, file }));
        setErrors((previous) => ({ ...previous, file: undefined }));
        setPreview(null);

        if (!file) {
            return;
        }

        setPreviewing(true);
        try {
            setPreview(await previewCertificateNameFile(file));
        } catch (error) {
            setErrors({ file: apiErrorMessage(error, t('admin.certificateNames.import.previewFailed')) });
        }
        setPreviewing(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.name.trim()) {
            setErrors({ name: t('admin.certificateNames.lists.nameRequired') });

            return;
        }

        if (mode === LIST_CREATE_EXCEL && !isEdit && !form.file) {
            setErrors({ file: t('admin.certificateNames.import.fileRequired') });

            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                const list = await updateCertificateNameList(record.id, form);
                toast.success(t('toast.success.updatedSuccessfully'));
                onSaved(list);
            } else {
                const created = await createCertificateNameList({
                    name: form.name.trim(),
                    description: form.description,
                    isActive: form.isActive,
                    names: mode === LIST_CREATE_MANUAL ? form.names : undefined,
                    category: form.category,
                    file: mode === LIST_CREATE_EXCEL ? form.file : undefined,
                });

                toast.success(
                    created.import
                        ? t('admin.certificateNames.lists.createdWithNames', {
                              name: created.list?.name,
                              count: created.import.imported,
                          })
                        : t('admin.certificateNames.lists.created', { name: created.list?.name }),
                );

                if (created.import) {
                    // Stay open on the summary; the page refreshes behind it.
                    setResult(created.import);
                    onSaved(created.list, { keepOpen: true });
                } else {
                    onSaved(created.list);
                }
            }
        } catch (error) {
            const fields = fieldErrors(error);

            if (Object.keys(fields).length) {
                setErrors(fields);
            } else {
                toast.error(apiErrorMessage(error, t('admin.certificateNames.lists.saveFailed')));
            }
        }
        setSaving(false);
    };

    const title = isEdit
        ? t('admin.certificateNames.lists.editTitle')
        : t(`admin.certificateNames.lists.createTitle.${mode ?? LIST_CREATE_EMPTY}`);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={mode === LIST_CREATE_EXCEL ? 'md' : 'sm'}>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers>
                    {result ? (
                        <ImportSummary summary={result} />
                    ) : (
                        <Box className="space-y-4">
                            <TextField
                                autoFocus
                                fullWidth
                                label={t('admin.certificateNames.lists.name')}
                                value={form.name}
                                onChange={set('name')}
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                                inputProps={{ maxLength: 255 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label={t('admin.certificateNames.lists.description')}
                                value={form.description}
                                onChange={set('description')}
                                error={Boolean(errors.description)}
                                helperText={errors.description}
                            />

                            {!isEdit && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.isActive}
                                            onChange={(event) => set('isActive')(event.target.checked)}
                                        />
                                    }
                                    label={t('admin.certificateNames.lists.activeOnCreate')}
                                />
                            )}

                            {!isEdit && mode === LIST_CREATE_MANUAL && (
                                <>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        label={t('admin.certificateNames.lists.namesLabel')}
                                        placeholder={t('admin.certificateNames.lists.namesPlaceholder')}
                                        value={form.names}
                                        onChange={set('names')}
                                        error={Boolean(errors.names)}
                                        helperText={
                                            errors.names ??
                                            t('admin.certificateNames.lists.namesHint', {
                                                count: form.names.split(/\r\n|\r|\n/).filter((line) => line.trim())
                                                    .length,
                                            })
                                        }
                                    />
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('admin.certificateNames.lists.namesCategory')}
                                        value={form.category}
                                        onChange={set('category')}
                                        SelectProps={{ displayEmpty: true }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value={NO_CERTIFICATE_CATEGORY}>
                                            {t('admin.certificateNames.noCategory')}
                                        </MenuItem>
                                        {CERTIFICATE_CATEGORIES.map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {t(`admin.certificateNames.categories.${value}`)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            )}

                            {!isEdit && mode === LIST_CREATE_EXCEL && (
                                <Box className="space-y-3">
                                    <Typography variant="body2" color="text.secondary">
                                        {t('admin.certificateNames.import.formatHint')}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileIcon />}
                                        disabled={previewing}
                                    >
                                        {form.file ? form.file.name : t('admin.certificateNames.import.chooseFile')}
                                        <input hidden type="file" accept={CERTIFICATE_FILE_ACCEPT} onChange={handleFile} />
                                    </Button>
                                    {previewing && (
                                        <Typography variant="body2">{t('admin.certificateNames.import.checking')}</Typography>
                                    )}
                                    {errors.file && (
                                        <Typography variant="body2" color="error">
                                            {errors.file}
                                        </Typography>
                                    )}
                                    <ImportSummary summary={preview} />
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {result ? (
                        <BtnBase fullWidth={false} onClick={onClose}>
                            {t('common.close', 'Close')}
                        </BtnBase>
                    ) : (
                        <>
                            <BtnBase variant="text" fullWidth={false} onClick={onClose}>
                                {t('common.cancel')}
                            </BtnBase>
                            <BtnBase
                                type="submit"
                                fullWidth={false}
                                isLoading={saving}
                                disabled={saving || previewing || (mode === LIST_CREATE_EXCEL && !isEdit && !preview)}
                            >
                                {isEdit ? t('common.update') : t('common.create')}
                            </BtnBase>
                        </>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CertificateListFormModal;
